import * as React from "react";

import { Check, ChevronRight, Lock } from "lucide-react";

import { cn } from "~/lib/utils";

// AIDEV-NOTE: ChecklistRow — generic task/step row for checklists.
// Pending: task icon + hover tint + chevron slide. Done: filled forest
// check + struck/subdued label. justDone: check-draw + pop animation
// (oc-pop / oc-draw keyframes in tailwind.config.ts). From the onboarding
// checklist design — see docs/specs/practitioner-onboarding-checklist.md.

export interface ChecklistRowProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    label: React.ReactNode;
    /** Icon shown in the indicator circle while the task is pending. */
    icon?: React.ReactNode;
    done?: boolean;
    /** Plays the check-draw + pop animation; set for ~700ms after completion. */
    justDone?: boolean;
    /** Locked (prerequisite not met): muted, not actionable, shows a lock. */
    locked?: boolean;
    /** Short reason shown on the right of a locked row (e.g. "After a consultation"). */
    hint?: React.ReactNode;
}

const ChecklistRow = React.forwardRef<HTMLButtonElement, ChecklistRowProps>(
    (
        {
            label,
            icon,
            done = false,
            justDone = false,
            locked = false,
            hint,
            className,
            disabled,
            ...props
        },
        ref,
    ) => (
        <button
            ref={ref}
            type="button"
            disabled={disabled ?? locked}
            aria-disabled={locked || undefined}
            className={cn(
                "group flex w-full items-center gap-[11px] rounded-[10px] px-[9px] py-2 text-left transition-colors duration-150",
                !done && !locked && "hover:bg-brand-forest-50",
                locked && "cursor-default",
                className,
            )}
            {...props}
        >
            <span
                className={cn(
                    "flex size-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors duration-200",
                    done
                        ? "border-brand-forest-800 bg-brand-forest-800 text-white"
                        : locked
                          ? "border-brand-forest-200 bg-white text-brand-forest-300"
                          : "border-brand-forest-300 bg-white text-brand-forest-500 group-hover:text-brand-forest-700",
                    justDone && "animate-oc-pop",
                )}
            >
                {done ? (
                    <Check
                        className={cn(
                            "size-[13px] stroke-[3]",
                            justDone &&
                                "animate-oc-draw [stroke-dasharray:24] [stroke-dashoffset:24]",
                        )}
                    />
                ) : locked ? (
                    <Lock className="size-[11px] stroke-2" />
                ) : (
                    <span className="[&>svg]:size-[12.5px] [&>svg]:stroke-2">
                        {icon}
                    </span>
                )}
            </span>
            <span
                className={cn(
                    "min-w-0 flex-1 font-body text-[13.5px] leading-[1.3] tracking-[-0.1px] transition-colors duration-200",
                    done
                        ? "font-normal text-brand-forest-400 line-through decoration-brand-forest-300"
                        : locked
                          ? "font-medium text-brand-forest-400"
                          : "font-medium text-brand-forest-800",
                )}
            >
                {label}
            </span>
            {locked && hint ? (
                <span className="shrink-0 font-body text-[11px] font-medium text-brand-forest-400">
                    {hint}
                </span>
            ) : null}
            {!done && !locked && (
                <ChevronRight className="size-[15px] shrink-0 stroke-2 text-brand-forest-300 transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-brand-forest-600" />
            )}
        </button>
    ),
);
ChecklistRow.displayName = "ChecklistRow";

export { ChecklistRow };
